// frontend/src/pages/AICoach.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    PaperAirplaneIcon,
    PlusIcon,
    TrashIcon,
    SparklesIcon,
    UserIcon,
    CpuChipIcon
} from '@heroicons/react/24/outline';

const renderFormattedInlineText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
            return (
                <strong key={index} className="font-semibold text-ink-950">
                    {part.slice(2, -2)}
                </strong>
            );
        }
        if (part.startsWith('*') && part.endsWith('*') && part.length >= 2 && !part.startsWith('**')) {
            return (
                <em key={index} className="italic text-ink-900">
                    {part.slice(1, -1)}
                </em>
            );
        }
        if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
            return (
                <code key={index} className="px-1.5 py-0.5 rounded bg-ink-100 font-mono text-xs text-brand-700">
                    {part.slice(1, -1)}
                </code>
            );
        }
        return part;
    });
};

const renderStructuredAssistantMessage = (content) => {
    if (!content) return null;

    const lines = content.split('\n');
    const sections = [];
    let currentSection = { title: null, items: [] };

    lines.forEach((line) => {
        const trimmed = line.trim();
        if (line.startsWith('### ') || line.startsWith('## ')) {
            if (currentSection.title || currentSection.items.length > 0) {
                sections.push(currentSection);
            }
            currentSection = { title: line.replace(/^#{2,4}\s+/, ''), items: [] };
        } else if (trimmed) {
            currentSection.items.push(line);
        }
    });
    if (currentSection.title || currentSection.items.length > 0) {
        sections.push(currentSection);
    }

    return (
        <div className="space-y-3">
            {sections.map((sec, sIdx) => {
                const titleLower = (sec.title || '').toLowerCase();
                let sectionTheme = 'border-border bg-white';
                let headerTheme = 'text-ink-900 border-border bg-ink-50';

                if (titleLower.includes('summary') || titleLower.includes('overview')) {
                    sectionTheme = 'border-brand-200 bg-brand-50/20';
                    headerTheme = 'text-brand-900 border-brand-200/60 bg-brand-50/50';
                } else if (titleLower.includes('insight') || titleLower.includes('discovery') || titleLower.includes('analysis')) {
                    sectionTheme = 'border-blue-200 bg-blue-50/20';
                    headerTheme = 'text-blue-900 border-blue-200/60 bg-blue-50/50';
                } else if (titleLower.includes('action') || titleLower.includes('step') || titleLower.includes('next')) {
                    sectionTheme = 'border-emerald-200 bg-emerald-50/20';
                    headerTheme = 'text-emerald-900 border-emerald-200/60 bg-emerald-50/50';
                } else if (titleLower.includes('question') || titleLower.includes('reflection')) {
                    sectionTheme = 'border-amber-200 bg-amber-50/20';
                    headerTheme = 'text-amber-900 border-amber-200/60 bg-amber-50/50';
                }

                return (
                    <div key={sIdx} className={`rounded-lg border overflow-hidden shadow-2xs ${sectionTheme}`}>
                        {sec.title && (
                            <h4 className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border-b flex items-center gap-1.5 ${headerTheme}`}>
                                {sec.title}
                            </h4>
                        )}
                        <div className="p-3 space-y-1.5">
                            {sec.items.map((item, iIdx) => {
                                const itemTrimmed = item.trim();
                                if (itemTrimmed.startsWith('- ') || itemTrimmed.startsWith('* ')) {
                                    return (
                                        <div key={iIdx} className="pl-1 text-sm flex items-start gap-2 text-ink-800">
                                            <span className="text-brand-600 font-bold select-none">•</span>
                                            <span className="flex-1 leading-relaxed">
                                                {renderFormattedInlineText(itemTrimmed.substring(2))}
                                            </span>
                                        </div>
                                    );
                                }
                                if (/^\d+\.\s/.test(itemTrimmed)) {
                                    const num = itemTrimmed.match(/^\d+\./)[0];
                                    const body = itemTrimmed.replace(/^\d+\.\s/, '');
                                    return (
                                        <div key={iIdx} className="pl-1 text-sm flex items-start gap-2 text-ink-800">
                                            <span className="font-bold text-brand-700 select-none">{num}</span>
                                            <span className="flex-1 leading-relaxed">
                                                {renderFormattedInlineText(body)}
                                            </span>
                                        </div>
                                    );
                                }
                                return (
                                    <p key={iIdx} className="text-sm text-ink-800 leading-relaxed">
                                        {renderFormattedInlineText(item)}
                                    </p>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const AICoach = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [conversations, setConversations] = useState([]);
    const [currentConversation, setCurrentConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [quickPrompts, setQuickPrompts] = useState([]);
    const [coachStatus, setCoachStatus] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [convsRes, promptsRes, statusRes] = await Promise.all([
                axios.get('/ai-coach/conversations'),
                axios.get('/ai-coach/quick-prompts'),
                axios.get('/ai-coach/status')
            ]);
            const convs = convsRes.data.data || [];
            setConversations(convs);
            setQuickPrompts(promptsRes.data.data || []);
            setCoachStatus(statusRes.data.data || null);

            if (convs.length > 0) {
                loadConversation(convs[0].id);
            } else {
                createNewConversation();
            }
        } catch (error) {
            toast.error('Failed to load AI Coach data');
        } finally {
            setLoading(false);
        }
    };

    const loadConversation = async (conversationId) => {
        try {
            const response = await axios.get(`/ai-coach/conversations/${conversationId}`);
            const data = response.data.data;
            setCurrentConversation(data.conversation);
            setMessages(data.messages || []);
        } catch (error) {
            toast.error('Failed to load conversation');
        }
    };

    const createNewConversation = async (title = 'New Conversation') => {
        try {
            const response = await axios.post('/ai-coach/conversations', { title });
            const newConv = response.data.data;
            setConversations([newConv, ...conversations]);
            setCurrentConversation(newConv);
            setMessages([{
                id: Date.now(),
                role: 'assistant',
                content: "Welcome to AI Coach! I'm here to help you with your internship journey. Ask me about learning, business discovery, process mapping, or career advice!",
                created_at: new Date().toISOString()
            }]);
            inputRef.current?.focus();
        } catch (error) {
            toast.error('Failed to create new conversation');
        }
    };

    const sendMessage = async () => {
        if (!inputMessage.trim() || !currentConversation) return;

        const message = inputMessage.trim();
        setInputMessage('');
        setSending(true);

        const optimisticMessage = {
            id: Date.now(),
            role: 'user',
            content: message,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, optimisticMessage]);

        try {
            const response = await axios.post(
                `/ai-coach/conversations/${currentConversation.id}/message`,
                { message }
            );

            const data = response.data.data;

            setMessages(prev => [...prev, data.message]);

            setConversations(prev => {
                const updated = prev.map(c => 
                    c.id === currentConversation.id 
                        ? { ...c, last_message_at: new Date(), message_count: (c.message_count || 0) + 2 }
                        : c
                );
                return updated;
            });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send message');
            setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const archiveConversation = async (conversationId) => {
        try {
            await axios.post(`/ai-coach/conversations/${conversationId}/archive`);
            setConversations(prev => prev.filter(c => c.id !== conversationId));
            if (currentConversation?.id === conversationId) {
                const remaining = conversations.filter(c => c.id !== conversationId);
                if (remaining.length > 0) {
                    loadConversation(remaining[0].id);
                } else {
                    createNewConversation();
                }
            }
            toast.success('Conversation archived');
        } catch (error) {
            toast.error('Failed to archive conversation');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-48" />
                <div className="skeleton h-96" />
                <span className="sr-only">Loading AI Coach…</span>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-8rem)] flex gap-4 w-full">
            {/* Sidebar */}
            <div className="w-64 flex-shrink-0 card overflow-hidden flex flex-col">
                <div className="p-3 border-b border-border flex justify-between items-center bg-ink-50">
                    <h2 className="text-sm font-semibold text-ink-950">Conversations</h2>
                    <button
                        type="button"
                        onClick={() => createNewConversation()}
                        className="p-1.5 bg-brand-700 text-white rounded-md hover:bg-brand-800 transition-colors"
                        title="New Conversation"
                        aria-label="New conversation"
                    >
                        <PlusIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {conversations.length === 0 ? (
                        <p className="text-sm text-ink-500 text-center py-4">
                            No conversations yet
                        </p>
                    ) : (
                        conversations.map(conv => (
                            <div
                                key={conv.id}
                                className={`p-2 rounded-lg cursor-pointer transition-colors flex justify-between items-center ${
                                    currentConversation?.id === conv.id ? 'bg-ink-100 font-medium' : 'hover:bg-ink-50 text-ink-600'
                                }`}
                                onClick={() => loadConversation(conv.id)}
                            >
                                <div className="flex-1 min-w-0 pr-1">
                                    <p className="text-xs font-semibold text-ink-950 truncate">
                                        {conv.title || 'New Conversation'}
                                    </p>
                                    <p className="text-[10px] text-ink-400">
                                        {new Date(conv.last_message_at || conv.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        archiveConversation(conv.id);
                                    }}
                                    className="text-ink-400 hover:text-ink-600 p-1"
                                    aria-label="Archive conversation"
                                >
                                    <TrashIcon className="w-3 h-3" aria-hidden="true" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
                <div className="p-3 border-t border-border bg-ink-50">
                    <div className="flex items-center justify-between gap-2 text-xs text-ink-500">
                        <div className="flex items-center space-x-2">
                            <CpuChipIcon className="w-4 h-4 text-ink-700" aria-hidden="true" />
                            <span>AI Coach</span>
                        </div>
                        <span
                            className={`rounded-full px-2 py-0.5 font-medium ${
                                coachStatus?.configured
                                    ? 'bg-brand-50 text-brand-800'
                                    : 'bg-ink-100 text-ink-600'
                            }`}
                            title={
                                coachStatus?.configured
                                    ? `OpenAI ${coachStatus.model || ''} active`
                                    : 'Add OPENAI_API_KEY in backend .env for live AI replies'
                            }
                        >
                            {coachStatus?.configured ? 'Live AI' : 'Guided mode'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 card overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-border flex justify-between items-center bg-ink-50/50">
                    <div className="flex items-center space-x-2">
                        <SparklesIcon className="w-5 h-5 text-brand-700" />
                        <h3 className="font-medium text-ink-950">
                            {currentConversation?.title || 'AI Coach Assistant'}
                        </h3>
                    </div>
                    {currentConversation && (
                        <button
                            onClick={() => archiveConversation(currentConversation.id)}
                            className="text-ink-400 hover:text-red-600 text-xs flex items-center space-x-1"
                            title="Archive conversation"
                        >
                            <TrashIcon className="w-4 h-4" />
                            <span>Archive</span>
                        </button>
                    )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 && (
                        <div className="text-center py-8">
                            <SparklesIcon className="w-12 h-12 mx-auto text-brand-500" />
                            <h3 className="mt-2 text-lg font-medium text-ink-950">
                                Welcome to AI Coach!
                            </h3>
                            <p className="text-sm text-ink-600 max-w-md mx-auto">
                                Ask me about learning modules, business AI assessments, process mapping, practical hours, or career advice.
                            </p>
                        </div>
                    )}

                    {messages.map((msg, index) => (
                        <div key={msg.id || index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`flex items-start space-x-2 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                                    msg.role === 'user' ? 'bg-brand-700 text-white' : 'bg-brand-50 text-brand-700'
                                }`}>
                                    {msg.role === 'user' ? <UserIcon className="w-4 h-4" /> : <CpuChipIcon className="w-4 h-4" />}
                                </div>
                                <div className={`px-4 py-3 rounded-lg text-sm ${
                                    msg.role === 'user'
                                        ? 'bg-brand-700 text-white rounded-tr-none'
                                        : 'bg-white text-ink-950 border border-border shadow-xs rounded-tl-none space-y-1'
                                }`}>
                                    {msg.role === 'user' ? (
                                        <div className="whitespace-pre-wrap">{msg.content}</div>
                                    ) : (
                                        renderStructuredAssistantMessage(msg.content)
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {sending && (
                        <div className="flex items-center space-x-2 text-ink-500">
                            <div className="w-7 h-7 rounded-full bg-brand-50 flex items-center justify-center">
                                <CpuChipIcon className="w-4 h-4 text-brand-700 animate-spin" />
                            </div>
                            <span className="text-xs text-ink-400">AI Coach is thinking...</span>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Quick Prompts */}
                {quickPrompts.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-100 flex flex-wrap gap-1.5 bg-ink-50/50">
                        {quickPrompts.slice(0, 4).map(prompt => (
                            <button
                                key={prompt.id}
                                onClick={() => setInputMessage(prompt.prompt_text)}
                                className="text-xs px-2.5 py-1 bg-white border border-border hover:bg-ink-100 rounded-full transition-colors flex items-center space-x-1"
                            >
                                <span>{prompt.icon}</span>
                                <span>{prompt.prompt_text}</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Input */}
                <div className="p-4 border-t border-border">
                    <div className="flex items-center space-x-2">
                        <textarea
                            ref={inputRef}
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Ask AI Coach a question..."
                            rows={1}
                            className="input-field flex-1 resize-none !mt-0"
                            disabled={sending}
                        />
                        <button
                            onClick={sendMessage}
                            disabled={!inputMessage.trim() || sending}
                            className="px-4 py-2 btn-primary transition-colors disabled:opacity-50 flex items-center space-x-1 text-sm"
                        >
                            <PaperAirplaneIcon className="w-4 h-4" />
                            <span>Send</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AICoach;
